import {Subscription} from 'rxjs';

import {globalWindow} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {
  EventLoopTask,
  fromEventLoopTasks,
} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {fromVisibilityEvent, isHidden} from '../../../libs/shared/core/src/helpers/rx/visibility';
import {ErrorWithData} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {getHasDeepPerformanceInstrumentation} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppContentTypesEnum} from '../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {AppPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {AppState} from '../../../src/store/appState';
import {AppStore} from '../../../src/store/appStore';
import {makeGetAppPanel} from '../../../src/store/panels/app/appPanelSelectors';
import {isFocused} from '../../rx/focus';
import {performance} from './performanceClient';
import {
  getHasPerformanceLogging,
  getHasPerformanceMetricLogging,
  getShouldEnableDataGroupInstrumentation,
} from './performanceMetricSelectors';

/*
 * Constants.
 */

export const eventLoopLogger = logger.getLogger('event-loop');

/*
 * State.
 */

let currentView: AppContentTypesEnum | undefined;
let lastVisibilityChange = 0;
let isEnabled: boolean = false;
let subscription: Subscription | undefined;
let isPerformanceMetric: boolean = false;
let hasDeepInstrumentation: boolean = false;
let hasDataGroupInstrumentation: boolean = false;

/*
 * Api.
 */

export function setupEventLoopLogging(store: AppStore, panelId: AppPanelId) {
  const handleStateUpdate = () => {
    const state = store.getState();
    if (getHasPerformanceLogging(state)) {
      maybeStartEventLoopLogging();
    } else {
      maybeStopEventLoopLogging();
    }

    maybeUpdateState(state, panelId);
  };

  store.subscribe(() => handleStateUpdate());
  handleStateUpdate();
}

function maybeStartEventLoopLogging() {
  if (isEnabled) {
    // Logging is already started.
    return;
  }

  isEnabled = true;

  // Subscribe to performance observer.
  subscription = new Subscription();
  subscription.add(
    // Subscribe to visibility changes.
    fromVisibilityEvent(globalWindow).subscribe(() => {
      lastVisibilityChange = performance.now();
    }),
  );
  subscription.add(
    // Subscribe to long tasks.
    fromEventLoopTasks().subscribe(
      (task) => {
        maybeReportLongTask(task);
      },
      (error) => {
        eventLoopLogger.error(
          error instanceof Error ? error.message : 'Tracing error',
          error instanceof ErrorWithData ? error.data : undefined,
        );
      },
    ),
  );
}

/** Resets event loop performance. Exported for testing. */
export function maybeStopEventLoopLogging() {
  if (!isEnabled) {
    // Logging is already stopped.
    return;
  }

  currentView = undefined;
  lastVisibilityChange = 0;
  isEnabled = false;
  subscription?.unsubscribe();
  subscription = undefined;
  isPerformanceMetric = false;
  hasDeepInstrumentation = false;
  hasDataGroupInstrumentation = false;
}

/*
 * Helpers.
 */

function maybeUpdateState(state: AppState, appPanelId: AppPanelId) {
  if (!isEnabled) {
    return;
  }

  const appPanel = makeGetAppPanel()(state, {panelId: appPanelId});
  currentView = appPanel.contentType;

  isPerformanceMetric = getHasPerformanceMetricLogging(state);
  hasDeepInstrumentation = getHasDeepPerformanceInstrumentation(state);
  hasDataGroupInstrumentation = getShouldEnableDataGroupInstrumentation(state);
}

function maybeReportLongTask(task: EventLoopTask) {
  const {startTime, duration, type, steps, timings, effectiveDuration} = task;

  if (!isEnabled) {
    // Logging is disabled.
    return;
  }

  if (isHidden(globalWindow) || startTime <= lastVisibilityChange) {
    // Ignore long tasks that occurred while the window was hidden.
    return;
  }

  eventLoopLogger.warn('Long task', {
    type,
    duration,
    effective_duration: effectiveDuration,
    is_focused: isFocused(globalWindow),
    current_view: currentView,
    steps,
    timings,
    is_performance_metric: isPerformanceMetric,
    has_deep_instrumentation: hasDeepInstrumentation,
    has_data_group_instrumentation: hasDataGroupInstrumentation,
  });
}
